import { Copy } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { StatCard } from '@/components/StatCard';
import { StatusBadge } from '@/components/StatusBadge';
import { formatAgo } from '@/lib/ago';
import { appUrls } from '@/lib/appUrls';
import { copyText } from '@/lib/copyText';
import { formatGb } from '@/lib/format';
import type { App } from '@/types/app';
import type { DomainSwitch } from '@/types/domain';
import { DomainSwitchStatus } from '@/components/apps/DomainSwitchStatus';
import { showSwitchStatus } from '@/lib/domainSwitch';

const linkClass =
  'rounded-sm text-primary underline-offset-4 hover:underline outline-none focus-visible:ring-2 focus-visible:ring-ring';

const TLS_LABELS: Record<string, string> = {
  letsencrypt: "Let's Encrypt",
  origin: 'Cloudflare origin certificate',
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card className="min-w-0 gap-3 p-4">
      <h2 className="text-base font-semibold">{title}</h2>
      {children}
    </Card>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
      <dt className="w-24 shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}

function When({ iso }: { iso: string | null | undefined }) {
  const t = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(t)) return <>—</>;
  return (
    <>
      {formatAgo(t)} <span className="text-muted-foreground">({new Date(t).toLocaleString()})</span>
    </>
  );
}

const finite = (n: number | undefined) => (typeof n === 'number' && Number.isFinite(n) ? n : null);

export function OverviewTab({
  app,
  domainSwitch = null,
  now,
}: {
  app: App;
  /** The app's latest domain switch, shown while open and for a day after it ended. */
  domainSwitch?: DomainSwitch | null;
  /** Time of the latest switch poll (keeps render pure). */
  now?: number;
}) {
  const urls = appUrls(app);
  const cpu = finite(app.stack?.cpu_limit);
  const mem = finite(app.stack?.memory_mb);
  const storage = finite(app.stack?.storage_gb);
  const tls = (app.tls_mode && TLS_LABELS[app.tls_mode]) || '—';

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {domainSwitch && now !== undefined && showSwitchStatus(domainSwitch, now) && <DomainSwitchStatus sw={domainSwitch} now={now} />}
      <Section title="Status & health">
        <dl className="space-y-1.5 text-sm">
          <Row label="Name"><span className="font-mono text-xs">{app.name}</span></Row>
          <Row label="ID"><span className="font-mono text-xs">{app.id}</span></Row>
          <Row label="Status"><StatusBadge status={app.status} /></Row>
          <Row label="Created"><When iso={app.created_at} /></Row>
          <Row label="Updated"><When iso={app.updated_at} /></Row>
          <Row label="Deployment">
            {app.deployment ? <StatusBadge status={app.deployment.status} /> : '—'}
          </Row>
        </dl>
      </Section>

      <Section title="Access">
        {urls.length === 0 ? (
          <p className="text-sm text-muted-foreground">No subdomain assigned</p>
        ) : (
          <ul className="space-y-1.5 text-sm">
            {urls.map((u) => (
              <li key={u.label} className="flex items-center justify-between gap-2">
                <a href={u.href} target="_blank" rel="noopener noreferrer" className={`${linkClass} min-w-0 break-all`}>
                  {u.host}
                </a>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Copy URL ${u.host}`}
                  onClick={() => void copyText(u.href)}
                >
                  <Copy className="size-4" aria-hidden />
                </Button>
              </li>
            ))}
          </ul>
        )}
        <dl className="space-y-1.5 text-sm">
          <Row label="Domain">{app.domain ? <span className="font-mono text-xs">{app.domain}</span> : '—'}</Row>
          <Row label="TLS">{tls}</Row>
        </dl>
      </Section>

      <div className="grid gap-4 sm:grid-cols-3 md:col-span-2">
        <StatCard label="CPU" value={cpu === null ? '—' : `${cpu} cores`} />
        <StatCard label="Memory" value={mem === null ? '—' : mem >= 1024 ? `${formatGb(mem)} GB` : `${mem} MB`} />
        <StatCard label="Storage" value={storage === null ? '—' : `${storage} GB`} />
      </div>

      <Section title="Placement">
        <dl className="space-y-1.5 text-sm">
          <Row label="Endpoint">
            {app.endpoint ? (
              <Link to={`/resources/endpoints/${app.endpoint.id}`} className={linkClass}>
                {app.endpoint.name || `Endpoint ${app.endpoint.id}`}
              </Link>
            ) : (
              '—'
            )}
          </Row>
          <Row label="URL">
            {app.endpoint?.url ? (
              <span className="block truncate font-mono text-xs" title={app.endpoint.url}>
                {app.endpoint.url}
              </span>
            ) : (
              '—'
            )}
          </Row>
        </dl>
      </Section>

      <Section title="Owner">
        <dl className="space-y-1.5 text-sm">
          <Row label="Name">{app.display_name || '—'}</Row>
          <Row label="External user">
            {app.external_user_id ? <span className="font-mono text-xs">{app.external_user_id}</span> : '—'}
          </Row>
        </dl>
      </Section>

      <Section title="Catalog">
        <dl className="space-y-1.5 text-sm">
          <Row label="Catalog">
            {app.app_catalog ? (
              <Link to={`/catalog/${app.app_catalog.id}`} className={linkClass}>
                {app.app_catalog.name}
              </Link>
            ) : (
              '—'
            )}
          </Row>
          <Row label="Stack">{app.stack?.name || '—'}</Row>
          <Row label="Image">
            {app.deployed_image ? <span className="font-mono text-xs break-all">{app.deployed_image}</span> : '—'}
          </Row>
        </dl>
      </Section>
    </div>
  );
}
